import React, { useEffect, useState } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  FormField,
  FormConfig,
  FormService,
} from "@shared/services/FormServices";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { useConfigCategoryJson, useFetchConsumerData } from "../../hooks";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { PAGE_SIZE } from "@shared/utils/constants";
import { logEvent } from "@shared/analytics/analytics";

type Props = {
  stepHelpers?: {
    getStepData: (index: number) => any;
    setStepData: (index: number, data: any) => void;
  };
  onNext?: () => void;
  onBack?: () => void;
  onPrevious?: () => void;
};

const NewListStepTwo: React.FC<Props> = ({
  stepHelpers,
  onNext,
  onPrevious,
}) => {
  const stepIndex = 1;
  const initialData = stepHelpers?.getStepData(stepIndex) || {};
  const remoteUtilityId = useRemoteUtilityId();
  const [currentPage, setCurrentPage] = useState(1);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [selectedType, setSelectedType] = useState<string | undefined>(
    undefined
  );
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedConsumers, setSelectedConsumers] = useState<string[]>(() => {
    // Initialize with recipient IDs from initial data, ensuring they're strings
    const ids = initialData?.recipientData?.recipientIds || [];
    return ids.map((id) => String(id));
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false); // Track if user has made any changes on step 2

  const { data: categoryList } = useConfigCategoryJson(
    "category_json",
    remoteUtilityId,
    "category",
    undefined,
    { enabled: !!remoteUtilityId }
  );

  const { data: subCategoryList } = useConfigCategoryJson(
    "category_json",
    remoteUtilityId,
    "sub_category",
    formValues?.category,
    { enabled: !!remoteUtilityId && !!formValues?.category }
  );

  const { data: areaList } = useConfigCategoryJson(
    "territory_json",
    remoteUtilityId,
    "area",
    "",
    { enabled: !!remoteUtilityId }
  );

  const { data: subAreaList } = useConfigCategoryJson(
    "territory_json",
    remoteUtilityId,
    "sub_area",
    formValues?.area,
    { enabled: !!remoteUtilityId && !!formValues?.area }
  );

  const { data: premiseList } = useConfigCategoryJson(
    "territory_json",
    remoteUtilityId,
    "premise",
    formValues?.subArea,
    { enabled: !!remoteUtilityId && !!formValues?.subArea }
  );

  // Modified consumer fetching logic:
  // 1. Don't fetch during initial edit load if source type is STATIC (unless user made changes)
  // 2. Fetch consumer data if filters are available and either:
  //    - User has made changes on step 2, OR
  //    - Source type is DYNAMIC (always fetch for dynamic), OR
  //    - Not initial load and source type is STATIC
  const shouldFetchConsumers =
    formValues.sourceType === "STATIC"
      ? // For STATIC: show data after initial load OR when user makes changes
        hasUserMadeChanges || !isInitialLoad
      : // For DYNAMIC: only show data when filters are selected AND user made changes
        !!selectedType && selectedOptions.length > 0 && hasUserMadeChanges; // For static, only fetch after initial load

  const { data: consumersList } = useFetchConsumerData(
    remoteUtilityId,
    currentPage,
    PAGE_SIZE,
    formValues.filterType,
    selectedType,
    selectedOptions,
    {
      enabled: shouldFetchConsumers,
    }
  );

  const consumers = consumersList?.results;
  const categoryOptions = mapToLabelValue(categoryList, "name", "code");
  const subCategoryOptions = mapToLabelValue(subCategoryList, "name", "code");
  const areaOptions = mapToLabelValue(areaList, "name", "code");
  const subAreaOptions = mapToLabelValue(subAreaList, "name", "code");
  const premiseOptions = mapToLabelValue(premiseList, "name", "code");

  const fields: FormField[] = [
    {
      name: "sourceType",
      type: "radio",
      label: "Source Type",
      required: true,
      options: [
        { label: "Static", value: "STATIC" },
        { label: "Dynamic", value: "DYNAMIC" },
      ],
    },
    {
      name: "filterType",
      type: "select",
      label: "Filter Type",
      required: false,
      options: [
        { label: "Category Based", value: "CATEGORY" },
        { label: "Location Based", value: "TERRITORY" },
      ],
      placeholder: "Select filter type",
    },
    {
      name: "category",
      type: "multi-select",
      label: "Category",
      required: false,
      placeholder: "Select category",
      showWhen: (formValues) => formValues.filterType === "CATEGORY",
      options: categoryOptions,
    },
    {
      name: "subCategory",
      type: "multi-select",
      label: "Subcategory",
      required: false,
      showWhen: (formValues) => formValues.filterType === "CATEGORY",
      placeholder: "Select subcategory",
      options: subCategoryOptions,
    },
    {
      name: "area",
      type: "multi-select",
      label: "Area",
      required: false,
      placeholder: "Select area",
      showWhen: (formValues) => formValues.filterType === "TERRITORY",
      options: areaOptions,
    },
    {
      name: "subArea",
      type: "multi-select",
      label: "Subarea",
      required: false,
      placeholder: "Select subarea",
      showWhen: (formValues) => formValues.filterType === "TERRITORY",
      options: subAreaOptions,
    },
    {
      name: "premise",
      type: "multi-select",
      label: "Premise",
      required: false,
      placeholder: "Select premise",
      options: premiseOptions,
      showWhen: (formValues) => formValues.filterType === "TERRITORY",
    },
  ];

  const config: FormConfig = {
    fields,
    layout: "grid",
    showSubmitButton: false,
    showResetButton: false,
    resetOnSubmit: false,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };

  const form = FormService.createForm(config);

  const handleSubmit = (formValues: any) => {
    const id = initialData.id;
    let stepData;

    switch (formValues.sourceType) {
      case "STATIC":
        stepData = {
          id,
          ...formValues,
          recipientData: {
            recipientIds: selectedConsumers || [], // Use consistent key
          },
        };
        break;

      case "DYNAMIC":
        stepData = {
          id,
          ...formValues,
          total_recipients: consumersList?.count || 0,
        };
        break;

      default:
        stepData = { id, ...formValues };
        break;
    }

    stepHelpers?.setStepData(stepIndex, stepData);
    onNext?.();
  };

  const handleReset = () => {
    form.reset();
    setSelectedConsumers([]);
    setSelectedType(undefined);
    setSelectedOptions([]);
    setHasUserMadeChanges(true); // Mark as changed when reset
  };

  const handleFieldChange = (name: string, value: any) => {
    // Mark that user has made changes on step 2
    setHasUserMadeChanges(true);
    logEvent(`List ${name} Field Update`, {
      fieldName: name,
      value
    })

    // Update form values first
    const newFormValues = { ...formValues, [name]: value };
    setFormValues(newFormValues);

    // Clear selected consumers and filters when switching source type
    if (name === "sourceType") {
      setSelectedConsumers([]);
      if (value !== formValues.sourceType) {
        setSelectedType(undefined);
        setSelectedOptions([]);

        // Clear all filter-related form fields when switching source types
        const clearedFormValues = {
          ...newFormValues,
          category: [],
          subCategory: [],
          area: [],
          subArea: [],
          premise: [],
          filterType: "", // Also clear filter type
        };
        setFormValues(clearedFormValues);

        // IMPORTANT: Don't call form.setValue here as it will cause infinite loop
        // The form will be updated through a separate effect
      }
    }

    // Clear selected consumers when filter type changes
    if (name === "filterType" && value !== formValues.filterType) {
      setSelectedConsumers([]);
      setSelectedType(undefined);
      setSelectedOptions([]);
    }

    // Only update selected type and options if we're not switching source types
    if (name !== "sourceType") {
      // Update selected type and options based on filter changes
      const currentFormValues = newFormValues;
      const currentFilterType =
        name === "filterType" ? value : currentFormValues.filterType;

      switch (currentFilterType) {
        case "CATEGORY":
          if (name === "subCategory" && value?.length > 0) {
            setSelectedType("sub_category");
            setSelectedOptions(value);
            setIsInitialLoad(false);
          } else if (name === "category" && value?.length > 0) {
            // Check if subCategory is empty in the new form values
            const hasSubCategory =
              name === "category"
                ? false
                : currentFormValues.subCategory?.length > 0;
            if (!hasSubCategory) {
              setSelectedType("category");
              setSelectedOptions(value);
              setIsInitialLoad(false);
            }
          }
          break;

        case "TERRITORY":
          if (name === "premise" && value?.length > 0) {
            setSelectedType("premise");
            setSelectedOptions(value);
            setIsInitialLoad(false);
          } else if (name === "subArea" && value?.length > 0) {
            // Check if premise is empty in the new form values
            const hasPremise =
              name === "subArea"
                ? false
                : currentFormValues.premise?.length > 0;
            if (!hasPremise) {
              setSelectedType("sub_area");
              setSelectedOptions(value);
              setIsInitialLoad(false);
            }
          } else if (name === "area" && value?.length > 0) {
            // Check if subArea is empty in the new form values
            const hasSubArea =
              name === "area" ? false : currentFormValues.subArea?.length > 0;
            if (!hasSubArea) {
              setSelectedType("area");
              setSelectedOptions(value);
              setIsInitialLoad(false);
            }
          }
          break;
      }
    }
  };

  // Separate effect to update form when formValues change
  useEffect(() => {
    if (!isInitialLoad) {
      // Update form fields without triggering onFieldChange
      FormService.populateForm(form, formValues, fields);
    }
  }, [formValues.sourceType, formValues.filterType]);

  // Load initial data - PRIORITIZE stepHelpers data over API data
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      // Populate form with initial data (prioritizing stepHelpers data)
      const formData = {
        sourceType: initialData.sourceType,
        filterType: initialData.filterType,
        category: initialData.category || [],
        subCategory: initialData.subCategory || [],
        area: initialData.area || [],
        subArea: initialData.subArea || [],
        premise: initialData.premise || [],
      };

      FormService.populateForm(form, formData, fields);
      setFormValues(formData);

      // Set initial selected type and options based on saved data
      if (initialData.filterType === "CATEGORY") {
        if (initialData.subCategory?.length > 0) {
          setSelectedType("sub_category");
          setSelectedOptions(initialData.subCategory);
        } else if (initialData.category?.length > 0) {
          setSelectedType("category");
          setSelectedOptions(initialData.category);
        }
      } else if (initialData.filterType === "TERRITORY") {
        if (initialData.premise?.length > 0) {
          setSelectedType("premise");
          setSelectedOptions(initialData.premise);
        } else if (initialData.subArea?.length > 0) {
          setSelectedType("sub_area");
          setSelectedOptions(initialData.subArea);
        } else if (initialData.area?.length > 0) {
          setSelectedType("area");
          setSelectedOptions(initialData.area);
        }
      }

      // For static lists, ensure the selected IDs are properly set
      if (
        initialData.sourceType === "STATIC" &&
        initialData.recipientData?.recipientIds?.length > 0
      ) {
        setSelectedConsumers(
          initialData.recipientData.recipientIds.map((id) => String(id))
        );
      }

      // After setting initial data, allow fetching new data
      setTimeout(() => setIsInitialLoad(false), 100);
    }
  }, []);

  // Handle pre-selection for edit mode when consumers load
  useEffect(() => {
    if (
      initialData?.sourceType === "STATIC" &&
      initialData?.recipientData?.recipientIds?.length > 0 &&
      consumers?.length > 0 &&
      selectedConsumers.length === 0
    ) {
      // Set the selected consumers based on the initial recipient IDs
      const idsToSelect = initialData.recipientData.recipientIds.map((id) =>
        String(id)
      );
      setSelectedConsumers(idsToSelect);
    }
  }, [consumers, initialData]);

  const DetailsRenderer = ({ details }: { details: any }) => {
    if (!details) return <div>NA</div>;

    const { category, subCategory, area, subArea, premise } = details;

    if (category || subCategory) {
      return (
        <div>
          {category && <div>{category}</div>}
          {subCategory && <div>{subCategory}</div>}
        </div>
      );
    }

    if (premise) {
      return <div>{premise}</div>;
    }

    if (area || subArea) {
      return (
        <div>
          {area && <div>{area}</div>}
          {subArea && <div>{subArea}</div>}
        </div>
      );
    }

    return <div>NA</div>;
  };
  console.log("CONUSMERMERSS", consumers);
  const mappedData = consumers?.map((consumer) => ({
    ...consumer,
    id: String(consumer.id), // Ensure ID is string for comparison
    details: consumer?.statusDisplay ?? "NA",
  }));

  const columns = [
    {
      header: "Name",
      key: "firstName",
    },
    {
      header: "Contact Info",
      key: "email",
    },
    {
      header: "Account Number",
      key: "consumerNo",
    },
    {
      header: "Status",
      key: "details",
    },
  ];

  const handleSelectedConsumers = (selectedIds: string[]) => {
    // Ensure all IDs are strings
    const stringIds = selectedIds.map((id) => String(id));
    setSelectedConsumers(stringIds);
    setHasUserMadeChanges(true); // Mark as changed when selection changes
  };

  const pagination = {
    currentPage,
    totalPages: Math.ceil((consumersList?.count || 0) / PAGE_SIZE),
    totalItems: consumersList?.count || 0,
    itemsPerPage: PAGE_SIZE,
    startItem: (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, consumersList?.count || 0),
  };

  const renderCustomFooter = () => (
    <div className="col-span-full flex flex-col gap-2 pt-6 border-t border-gray-200">
      {formValues.sourceType === "STATIC" ? (
        <>
          <GenericTable
            data={mappedData || []}
            columns={columns}
            emptyMessage="Please select filters to be able to see data"
            rowKey={"id"}
            selectable={true}
            selectedRows={selectedConsumers}
            onSelectionChange={(rowIds) => {
              handleSelectedConsumers(rowIds);
            }}
            onPageChange={(page) => setCurrentPage(page)}
            pagination={pagination}
          />
          {selectedConsumers.length > 0 && (
            <div className="text-sm font-medium text-gray-700 bg-gray-50 p-2 rounded">
              Selected: {selectedConsumers.length} contacts
            </div>
          )}
        </>
      ) : formValues.sourceType === "DYNAMIC" ? (
        <>
          <GenericTable
            data={mappedData || []}
            columns={columns}
            rowKey={"id"}
            onPageChange={(page) => setCurrentPage(page)}
            pagination={pagination}
          />
          {consumersList?.count > 0 && (
            <div className="text-sm font-medium text-gray-700 bg-gray-50 p-2 rounded">
              Total matching contacts: {consumersList.count}
            </div>
          )}
        </>
      ) : (
        <p>Please select a source type and filters to view the consumer list</p>
      )}
      <div className="flex justify-between">
        <Button variant="outline" type="button" onClick={onPrevious}>
          Back
        </Button>
        <Button type="submit">Next</Button>
      </div>
    </div>
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <DynamicForm
        fields={fields}
        form={form}
        config={config}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={(fieldName, value) => handleFieldChange(fieldName, value)}
        gridColumns={1}
        responsiveColumns={{ sm: 1, md: 2 }}
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};

export default NewListStepTwo;
